import { useState, useCallback, useEffect } from "react";
import { styled } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  Avatar,
  Box,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  Toolbar,
} from "@mui/material";
import MuiAppBar from "@mui/material/AppBar";
import LeftMenuDrawer from "./leftMenuDrawer";
import logo from "../assets/images/newLogo.png";
import user from "../assets/images/user.jpg";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ComponentBackdrop from "../components/backdrop";
import toast from 'react-hot-toast';
import axios from 'axios';
import { useApiClient } from '../context/ApiClientContext';
import api from "../helper/baseApi";
import { globalEvent } from "../helper/globalEvents";
import { Menu } from "@mui/icons-material";

const drawerWidth = 210;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isMobile",
})(({ theme, open, isMobile }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && !isMobile && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const PrivateLayout = ({ children }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(!isMobile);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [userProfile, setUserProfile] = useState({
    employeeId: 0,
    avatarImg: "",
    name: "",
    email: "",
    phone: "",
    dateJoin: ""
  });
  const { apiUrls: { mobileApiUrl } } = useApiClient();

  const toggleDrawer = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  // 👇 Automatically close the drawer on mobile
  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isMobile]);

  useEffect(() => {
    getProfile();
  }, []);

  const getProfile = async () => {
    try {
      const apiUrl = `${mobileApiUrl}user/profile`;
      const response = await api.get(apiUrl);
      if (response.status == 200) {
        setUserProfile({
          employeeId: response.data.employeeId,
          avatarImg: response.data.avatarImg,
          name: response.data.name,
          email: response.data.email,
          phone: response.data.phone,
          dateJoin: response.data.dateJoin.split("T")[0]
        });
      }
    }
    catch (error) {
      console.log(error);
      if (error.status === 404 || error.status === 400) {
        if (error.response.data.errorMesage) {
          toast.error(error.response.data.errorMesage);
        } else if (error.response.data.message.error) {
          toast.error(error.response.data.message.error.message);
        } else {
          toast.error("Oops! Something went wrong while fetching user data.");
        }
      }
      else {
        toast.error("Something went wrong while fetching user data.");
      }
    }
  }

  useEffect(() => {
    const handleStart = () => setOpenBackdrop(true);
    const handleEnd = () => setOpenBackdrop(false);

    globalEvent.on("refresh:start", handleStart);
    globalEvent.on("refresh:end", handleEnd);

    return () => {
      globalEvent.off("refresh:start", handleStart);
      globalEvent.off("refresh:end", handleEnd);
    };
  }, []);

  return (
    <>
      <Box sx={{ display: "flex", overflow: "hidden", mt:2 }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          open={open}
           isMobile={isMobile}
          sx={{ 
            width: "100%",
            // boxShadow: 'none'
            boxShadow: "0px 2px 4px rgba(189, 189, 189, 0.35)",
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              backgroundColor: "background.main",
              minHeight: 64, // define a consistent AppBar height
              paddingX: 2,
            }}
          >
            {isMobile && (
              <IconButton onClick={toggleDrawer}>
                <Menu />
              </IconButton>
            )}
            {/* <Stack direction="column" alignItems="center"> */}
              <Box
                component="img"
                src={logo}
                alt="Logo"
                sx={{
                  width: { xs: 35, md: 50 },
                  height: "auto",
                  // mb: 2,
                }}
              />

            <Stack direction="column" alignItems="center">

            {!isMobile && <Typography variant="subtitle" color="text.secondary" fontWeight={700} sx={{ ml: 1 }}>
              POISUM Portal
            </Typography>
            }
            <Typography variant="note" color="text.secondary" fontWeight={'semibold'} fontStyle={'italic'} textAlign={'right'}>
              {!isMobile ? 'Version 1.1.1' : 'V1.1.1'}
            </Typography>
            </Stack>

            <Box sx={{ flexGrow: 1 }} />

            <Stack direction="row" alignItems="center" spacing={2} margin={2} textAlign="right" >
              <Typography color="primary.main" variant="body" textTransform={"capitalize"}>
                {userProfile.name}, Welcome to Poisum Portal
              </Typography>
              <Avatar
                alt="User Avatar"
                src={userProfile.avatarImg}
                sx={{ width: isMobile ? 30 : 40, height: isMobile ? 30 : 40, }}
              />
            </Stack>
          </Toolbar>
        </AppBar>
        <LeftMenuDrawer
          open={open}
          toggleDrawerCallback={toggleDrawer}
        />

        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.main,
            flexGrow: 1,
            height: "100vh",
            overflowY: "scroll",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Toolbar />
          <Box >{children}</Box>
        </Box>
      </Box>

      <ComponentBackdrop openBackdrop={openBackdrop} />
    </>
  );
};

export default PrivateLayout;
